import React, { useContext, useEffect, useId, useState } from "react";
import { FormControl, InputLabel, MenuItem, Select } from "@mui/material";
import { DataContext } from "../../../context/DataContext";
import { getToken } from "../../../api/Account";
import { getGestoresAsync } from "../../../api/Users";
import { useAviso } from "../../base";
import { simpleMessage } from "../../../helpers/Helpers";

/**
 * Lista de gestores para los selectores de reportes. Carga la lista al montarse
 * (getGestoresAsync) y ofrece la opción "TODOS..." con el valor "t".
 * Si la carga falla: 401 abre la pantalla de sesión no autorizada; otro error, un aviso.
 *
 * @param {object} props
 * @param {string|number} props.value Id del gestor elegido, "t" para todos, "" si aún no eligió.
 * @param {(valor: string|number) => void} props.onChange
 * @param {string} [props.label] Etiqueta del campo (por defecto "Gestores").
 * @param {boolean} [props.error] Marca el campo como inválido.
 */
export const SelectorGestor = ({
  value,
  onChange,
  label = "Gestores",
  error = false,
}) => {
  const id = useId();
  const aviso = useAviso();
  const { setIsLoading, setIsUnautorized } = useContext(DataContext);
  const [gestores, setGestores] = useState([]);

  useEffect(() => {
    (async () => {
      let result;
      setIsLoading(true);
      try {
        result = await getGestoresAsync(getToken());
      } catch {
        // Sin red, getGestoresAsync lanza (errorResponse lee response.status de undefined)
        // y la pantalla de carga quedaba encendida.
        aviso.error("No se pudo conectar con el servidor");
        return;
      } finally {
        setIsLoading(false);
      }
      if (!result.statusResponse) {
        if (result.error === "Unauthorized") {
          setIsUnautorized(true);
          return;
        }
        // Como en el resto del rediseño, este caso sigue con Swal (DESIGN.md).
        if (result.error === "Sesión activa en otro dispositivo") {
          simpleMessage(result.error, "error");
          return;
        }
        aviso.error(result.error);
        return;
      }
      setGestores(result.data.result);
    })();
  }, [setIsLoading, setIsUnautorized, aviso]);

  return (
    <FormControl fullWidth error={error} sx={{ textAlign: "left" }}>
      <InputLabel id={`${id}-label`}>{label}</InputLabel>
      <Select
        labelId={`${id}-label`}
        id={id}
        value={value}
        label={label}
        onChange={(e) => onChange(e.target.value)}
        sx={{ textAlign: "left" }}
      >
        {gestores.map((item) => (
          <MenuItem key={item.id} value={item.id}>
            {item.fullName}
          </MenuItem>
        ))}
        <MenuItem value="t">TODOS...</MenuItem>
      </Select>
    </FormControl>
  );
};
